import React, { useEffect, useState } from "react";
import {
    Button,
    Col,
    DatePicker,
    Row,
    Select,
    Spin,
    Tooltip,
    Card,
    Form,
} from "antd";
import "./GeneralLedger.css";
import { getAllOpeningBalances } from "./GeneralLedgerService";
import { exportBranchWiseLedger } from "../../Utils/exportBranchWiseLedger";
import { getCurrentDateTimeString } from "../../Utils/Util";

const { Option } = Select;

const LedgerBranchWise = ({
    loginPlaceId,
    headerData = [],
    districtData = [],
    districtName,
    divisionalId,
}) => {
    const [formState, setFormState] = useState({
        districts: [],
        divisionalAreas: [],
        branches: [],
        header: null,
        ledgerAccounts: [],
        date: null,
    });

    const [availableDivisionalAreas, setAvailableDivisionalAreas] = useState([]);
    const [availableBranches, setAvailableBranches] = useState([]);
    const [loading, setLoading] = useState(false);

    // Helper to update formState fields easily
    const updateFormState = (field, value) => {
        setFormState((prev) => ({ ...prev, [field]: value }));
    };

    // ✅ Update available divisional areas when districts change
    useEffect(() => {
        let filteredDivisionalAreas = [];

        if (loginPlaceId === "2" && divisionalId) {
            filteredDivisionalAreas = districtData
                .flatMap((d) => d.divisionalAreaDtoList || [])
                .filter((div) => div.divAId === divisionalId);

            updateFormState("divisionalAreas", [divisionalId]);
        } else {
            filteredDivisionalAreas = districtData
                .filter((d) => formState.districts.includes(d.districtId))
                .flatMap((d) => d.divisionalAreaDtoList || []);

            updateFormState(
                "divisionalAreas",
                formState.divisionalAreas.filter((did) =>
                    filteredDivisionalAreas.some((d) => d.divAId === did)
                )
            );
        }

        setAvailableDivisionalAreas(filteredDivisionalAreas);
    }, [formState.districts, districtData, loginPlaceId, divisionalId]);

    // ✅ Update branches when divisional areas change
    useEffect(() => {
        const filteredBranches = districtData
            .flatMap((d) => d.divisionalAreaDtoList || [])
            .filter((div) => formState.divisionalAreas.includes(div.divAId))
            .flatMap((div) => div.branchDtoList || []);

        setAvailableBranches(filteredBranches);
        updateFormState(
            "branches",
            formState.branches.filter((bid) =>
                filteredBranches.some((b) => b.id === bid)
            )
        );
    }, [formState.divisionalAreas, districtData]);

    // ✅ Handle header change and clear ledger accounts
    const handleHeaderChange = (value) => {
        updateFormState("header", value);
        updateFormState("ledgerAccounts", []);
    };

    // ✅ Ledger account select logic (supports "Select All")
    const handleLedgerAccChangeReport = (values) => {
        const ledgerAccList =
            headerData.find((h) => h.headerId === parseInt(formState.header))
                ?.ledgerAccDtoList || [];

        if (values.includes("all")) {
            if (formState.ledgerAccounts.length === ledgerAccList.length) {
                updateFormState("ledgerAccounts", []);
            } else {
                updateFormState(
                    "ledgerAccounts",
                    ledgerAccList.map((acc) => acc.legId)
                );
            }
        } else {
            updateFormState("ledgerAccounts", values);
        }
    };

    // ✅ Get district names (for report title/export)
    const getSelectedDistrictNames = () => {
        return districtData
            .filter((d) => formState.districts.includes(d.districtId))
            .map((d) => d.districtName)
            .join(", ");
    };

    // ✅ Get Divisional area names (for report title/export)
    const getSelectedDivisionalAreaNames = () => {
        return districtData
            .flatMap((d) => d.divisionalAreaDtoList || [])
            .filter((div) => formState.divisionalAreas.includes(div.divAId))
            .map((div) => div.divAIdName)
            .join(", ");
    };



    // ✅ Handle "Generate Report" click
    const handleBookEntryListApi = () => {
        const data = {
            branchIdList: formState.branches,
            ledgerIdList: formState.ledgerAccounts,
            selectedDate: formState.date
                ? formState.date.format("YYYY-MM-DD")
                : null,
        };
        const title = loginPlaceId === "2" ? getSelectedDivisionalAreaNames() : getSelectedDistrictNames();
        console.log("title", title);

        setLoading(true);
        getAllOpeningBalances(data)
            .then((res) => {
                exportBranchWiseLedger({
                    data: res.data,
                    fileName:
                        "branch-ledger-" + getCurrentDateTimeString(),
                    selectedDate: data.selectedDate,
                    title: title,
                });
            })
            .catch((err) => console.error(err))
            .finally(() => setLoading(false));
    };

    // ✅ Button disable condition
    const isGenerateDisabled = !(
        formState.branches.length > 0 &&
        formState.header &&
        formState.ledgerAccounts.length > 0 &&
        formState.date
    );

    return (
        <div className="panel-container">
            <Card hoverable className="inner-card">
                <Form layout="vertical">
                    <Row gutter={[16, 16]} justify="space-around">
                        {(loginPlaceId === "3" || loginPlaceId === "4") && (
                            <Col span={24}>
                                <Form.Item label="Select Districts">
                                    <Select
                                        mode="multiple"
                                        placeholder="Select districts"
                                        style={{ width: "100%" }}
                                        value={formState.districts}
                                        onChange={(values) =>
                                            updateFormState("districts", values)
                                        }
                                    >
                                        {districtData
                                            .filter((d) =>
                                                loginPlaceId === "4"
                                                    ? d.districtName ===
                                                    districtName
                                                    : true
                                            )
                                            .map((d) => (
                                                <Option
                                                    key={d.districtId}
                                                    value={d.districtId}
                                                >
                                                    🏙️ {d.districtName}
                                                </Option>
                                            ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        )}

                        <Col span={24}>
                            <Form.Item label="Select Divisional Areas">
                                <Select
                                    mode="multiple"
                                    placeholder="Select divisional areas"
                                    style={{ width: "100%" }}
                                    value={formState.divisionalAreas}
                                    onChange={(values) => {
                                        if (values.includes("ALL")) {
                                            updateFormState(
                                                "divisionalAreas",
                                                availableDivisionalAreas.map(
                                                    (d) => d.divAId
                                                )
                                            );
                                        } else {
                                            updateFormState(
                                                "divisionalAreas",
                                                values
                                            );
                                        }
                                    }}
                                    disabled={
                                        availableDivisionalAreas.length === 0
                                    }
                                >
                                    {(loginPlaceId === "3" ||
                                        loginPlaceId === "4") && (
                                            <Option key="ALL" value="ALL">
                                                🔘 Select All Divisional Areas
                                            </Option>
                                        )}
                                    {availableDivisionalAreas.map((d) => (
                                        <Option key={d.divAId} value={d.divAId}>
                                            🏙️ {d.divAIdName}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>

                        <Col span={24}>
                            <Form.Item label="Select Branch(es)">
                                <Select
                                    mode="multiple"
                                    placeholder="Select branches"
                                    style={{ width: "100%" }}
                                    value={formState.branches}
                                    onChange={(values) => {
                                        if (values.includes("ALL")) {
                                            updateFormState(
                                                "branches",
                                                availableBranches.map(
                                                    (b) => b.id
                                                )
                                            );
                                        } else {
                                            updateFormState("branches", values);
                                        }
                                    }}
                                    disabled={availableBranches.length === 0}
                                >
                                    <Option key="ALL" value="ALL">
                                        🔘 Select All Branches
                                    </Option>
                                    {availableBranches.map((branch) => (
                                        <Option
                                            key={branch.id}
                                            value={branch.id}
                                        >
                                            🏢 {branch.branchName}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>

                        <Col span={24}>
                            <Form.Item label="Header">
                                <Select
                                    value={formState.header}
                                    onChange={handleHeaderChange}
                                    style={{ width: "100%" }}
                                >
                                    <Option value="">-- Select Header --</Option>
                                    {headerData.map((header) => (
                                        <Option
                                            key={header.headerId}
                                            value={header.headerId}
                                        >
                                            {header.headerName}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>

                        <Col span={24}>
                            <Form.Item label="Account">
                                <Select
                                    mode="multiple"
                                    allowClear
                                    placeholder="Select ledger accounts"
                                    value={formState.ledgerAccounts}
                                    onChange={handleLedgerAccChangeReport}
                                    style={{ width: "100%" }}
                                    disabled={!formState.header}
                                >
                                    <Option key="all" value="all">
                                        <b>🔘 Select All Accounts</b>
                                    </Option>
                                    {headerData
                                        .find((header) => header.headerId === parseInt(formState.header))
                                        ?.ledgerAccDtoList
                                        // 1. Create a shallow copy to avoid mutating original state
                                        .slice()
                                        // 2. Sort by legCode
                                        .sort((a, b) => a.legCode.localeCompare(b.legCode, undefined, { numeric: true }))
                                        // 3. Map the sorted list
                                        .map((ledgerAcc) => (
                                            <Option key={ledgerAcc.legId} value={ledgerAcc.legId}>
                                                🗺️
                                                <Tooltip
                                                    title={`${ledgerAcc.legCode}-${ledgerAcc.legName}`}
                                                    placement="left"
                                                >
                                                    <span>
                                                        {ledgerAcc.legCode} - {ledgerAcc.legName}
                                                    </span>
                                                </Tooltip>
                                            </Option>
                                        ))}
                                </Select>
                            </Form.Item>
                        </Col>

                        <Col span={24}>
                            <Form.Item label="Date">
                                <DatePicker
                                    onChange={(date) =>
                                        updateFormState("date", date)
                                    }
                                    style={{ width: "100%" }}
                                />
                            </Form.Item>
                        </Col>

                        <Col span={24} style={{ textAlign: "right" }}>
                            <Form.Item className="generate-btn-outline">
                                <Button
                                    onClick={handleBookEntryListApi}
                                    disabled={loading || isGenerateDisabled}
                                    className="generate-btn"
                                >
                                    {loading ? <Spin /> : "Generate Report"}
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Card>
        </div>
    );
};

export default LedgerBranchWise;
